import type { InsightsDonutSegment } from "../../../shared/layout/InsightsDonutCard";
import type {
  BucketInsightsResponse,
  CountsInsightsResponse,
  InsightsDatum,
} from "./trialInsightsService";
import type { BreakdownChartConfig, CountsChartConfig } from "./types";

/** Fallback for a label with no configured colour. */
const FALLBACK_COLOR = "#9CA3AF";

/** Uppercases the first character and leaves the rest of the string alone. */
export const capitalizeFirst = (value: string): string =>
  value.length > 0
    ? `${value.charAt(0).toUpperCase()}${value.slice(1)}`
    : value;

/**
 * Display copy for a raw API label.
 *
 * Looks up `<keyPrefix>.<label>` first so copy stays translatable, and falls
 * back to the label with its first letter capitalised. The fallback means a
 * status the API adds before the translations catch up still renders as
 * something readable instead of a blank cell.
 */
export const resolveInsightLabel = (
  translate: (key: string) => string,
  label: string,
  keyPrefix?: string
): string => {
  if (keyPrefix) {
    const translated = translate(`${keyPrefix}.${label}`);
    if (translated) {
      return translated;
    }
  }

  return capitalizeFirst(label);
};

/** Sum of every slice — the whole a breakdown is measured against. */
export const bucketTotal = (data: InsightsDatum[] | undefined): number =>
  (data ?? []).reduce((total, datum) => total + datum.value, 0);

/**
 * Turns a bucketed response into donut segments, in the configured order.
 *
 * `displayOrder` doubles as an allow-list, so an unexpected label is dropped
 * rather than drawn in a fallback grey nobody can interpret.
 */
export const toDonutSegments = (
  response: BucketInsightsResponse | undefined,
  config: BreakdownChartConfig,
  translate: (key: string) => string
): InsightsDonutSegment[] => {
  if (!response) return [];

  const byLabel = new Map(
    response.data.map((datum) => [datum.label, datum.value])
  );

  return config.displayOrder
    .filter((label) => byLabel.has(label))
    .map((label) => ({
      id: label,
      label: resolveInsightLabel(translate, label, config.labelKeyPrefix),
      value: byLabel.get(label) ?? 0,
      color: config.colors[label] ?? FALLBACK_COLOR,
    }));
};

export interface InsightCounts {
  value: number;
  total: number;
}

/** Reads a numeric field, treating anything non-numeric as zero. */
const numericField = (
  response: CountsInsightsResponse,
  field: string
): number => {
  const raw = response[field];
  return typeof raw === "number" && Number.isFinite(raw) ? raw : 0;
};

/**
 * Reads the two named counts a `counts` endpoint returns.
 *
 * A missing or renamed field reads as zero rather than throwing — that should
 * degrade to an honest "0", not a crashed card.
 */
export const toInsightCounts = (
  response: CountsInsightsResponse | undefined,
  config: CountsChartConfig
): InsightCounts => {
  if (!response) return { value: 0, total: 0 };

  return {
    value: numericField(response, config.valueField),
    total: numericField(response, config.totalField),
  };
};

/** English ordinal suffix for a day of the month: 1st, 2nd, 3rd, 4th, 11th … */
const englishOrdinal = (day: number): string => {
  // 11-13 are the exceptions that break the last-digit rule.
  if (day % 100 >= 11 && day % 100 <= 13) return "th";

  switch (day % 10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};

/**
 * Formats an insights timestamp as the day it describes, e.g. "July 9th".
 *
 * Formatted in UTC, deliberately. `retrieved_at` is the server's own instant
 * and the card names the day the server computed "planned" against — reading
 * it in the viewer's zone would show the neighbouring day for anyone far
 * enough east or west of a near-midnight timestamp, disagreeing with the
 * number beside it.
 *
 * The ordinal suffix is English-only — no other language forms dates that way —
 * so other locales get their own `Intl` month/day formatting without one.
 *
 * Returns "" for a missing or unparseable timestamp so a caller can fall back
 * to a static title rather than render "Invalid Date".
 */
export const formatInsightDate = (
  timestamp: string | undefined,
  locale: string = "en"
): string => {
  if (!timestamp) return "";

  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return "";

  if (locale.toLowerCase().startsWith("en")) {
    const month = new Intl.DateTimeFormat(locale, {
      month: "long",
      timeZone: "UTC",
    }).format(date);
    const day = date.getUTCDate();

    return `${month} ${day}${englishOrdinal(day)}`;
  }

  return new Intl.DateTimeFormat(locale, {
    month: "long",
    day: "numeric",
    timeZone: "UTC",
  }).format(date);
};
